import { useState, useRef, useEffect } from "react";
import { categories } from "@/mocks/categories";

const categoryGroups = [
  {
    label: "Apparel",
    cats: [0, 1, 2, 3, 4],
  },
  {
    label: "Headwear & Sets",
    cats: [5, 6],
  },
  {
    label: "Essentials",
    cats: [7, 8],
  },
  {
    label: "Accessories",
    cats: [9, 10, 11, 12],
  },
  {
    label: "More",
    cats: [13],
  },
];

export default function Navbar() {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeMobileGroup, setActiveMobileGroup] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const groupIcons: Record<string, string> = {
    "Apparel": "ri-t-shirt-line",
    "Headwear & Sets": "ri-base-station-line",
    "Essentials": "ri-checkbox-blank-line",
    "Accessories": "ri-vip-diamond-line",
    "More": "ri-more-line",
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? "bg-background-50/95 backdrop-blur-md shadow-sm"
          : "bg-transparent"
      }`}
    >
      <div className="w-full px-4 md:px-6 lg:px-10">
        <div className="flex items-center justify-between h-16 md:h-18">
          <div className="flex items-center gap-6">
            <a
              href="/"
              className="flex items-center gap-3 cursor-pointer no-underline"
            >
              <div className="w-9 h-9 rounded-full bg-primary-500 flex items-center justify-center">
                <span className="text-background-50 font-heading text-lg font-bold">K</span>
              </div>
              <span className="font-heading text-xl font-semibold text-foreground-950 whitespace-nowrap">
                Kakobuy Finds
              </span>
            </a>

            <div className="hidden md:flex items-center gap-4 ml-4">
              <a
                href="/"
                className="text-sm text-foreground-700 hover:text-foreground-950 transition-colors duration-200 whitespace-nowrap"
              >
                Home
              </a>
              <a
                href="/article/kakobuy-spreadsheet-guide"
                className="text-sm text-foreground-700 hover:text-foreground-950 transition-colors duration-200 whitespace-nowrap"
              >
                Guide
              </a>
              <a
                href="/article/cssbuy-spreadsheet-guide"
                className="text-sm text-foreground-700 hover:text-foreground-950 transition-colors duration-200 whitespace-nowrap"
              >
                Comparisons
              </a>
              <div ref={dropdownRef} className="relative">
                <button
                  onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                  className="flex items-center gap-2 px-4 py-2 text-sm rounded-full border border-foreground-300/60 text-foreground-700 hover:border-foreground-500 hover:text-foreground-950 transition-all duration-200 whitespace-nowrap cursor-pointer bg-transparent"
                >
                  <span>All Categories</span>
                  <i
                    className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${
                      isDropdownOpen ? "rotate-180" : ""
                    }`}
                  ></i>
                </button>

                {isDropdownOpen && (
                  <div className="absolute top-full mt-2 left-0 w-64 bg-background-50 rounded-lg border border-background-200/70 py-2 max-h-[420px] overflow-y-auto z-50">
                    {categories.map((cat) => (
                      <a
                        key={cat.slug}
                        href={cat.url}
                        target="_blank"
                        rel="noopener nofollow"
                        className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-accent-100 hover:text-accent-800 transition-colors duration-150 cursor-pointer whitespace-nowrap"
                        onClick={() => setIsDropdownOpen(false)}
                      >
                        <span className="w-5 h-5 flex items-center justify-center">
                          <i className={`${cat.icon} text-sm`}></i>
                        </span>
                        {cat.name}
                      </a>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="hidden md:flex items-center gap-3">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-5 py-2 text-sm border border-foreground-300/60 text-foreground-800 rounded-full hover:border-foreground-500 hover:text-foreground-950 transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              Kakobuy Sheet
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-5 py-2 text-sm bg-primary-500 text-background-50 rounded-full hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              Buy It Now
            </a>
          </div>

          <button
            className="md:hidden w-10 h-10 flex items-center justify-center cursor-pointer bg-transparent border-none rounded-full hover:bg-background-100 transition-colors"
            onClick={() => {
              setIsMobileMenuOpen(!isMobileMenuOpen);
              if (isMobileMenuOpen) setActiveMobileGroup(null);
            }}
            aria-label="Toggle menu"
          >
            <i
              className={`text-xl text-foreground-800 transition-transform duration-200 ${
                isMobileMenuOpen ? "ri-close-line rotate-90" : "ri-menu-line"
              }`}
            ></i>
          </button>
        </div>

        {isMobileMenuOpen && (
          <div className="md:hidden bg-background-50 border-t border-background-200/70 pb-6 pt-3 max-h-[calc(100vh-64px)] overflow-y-auto">
            <div className="flex flex-col gap-1">
              <a
                href="/"
                className="flex items-center gap-3 px-4 py-3.5 text-sm font-medium text-foreground-800 hover:bg-secondary-50 rounded-lg transition-colors"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <span className="w-8 h-8 rounded-full bg-accent-100 flex items-center justify-center">
                  <i className="ri-home-4-line text-accent-600 text-sm"></i>
                </span>
                Home
              </a>

              <a
                href="/article/kakobuy-spreadsheet-guide"
                className="flex items-center gap-3 px-4 py-3.5 text-sm font-medium text-foreground-800 hover:bg-secondary-50 rounded-lg transition-colors"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <span className="w-8 h-8 rounded-full bg-accent-100 flex items-center justify-center">
                  <i className="ri-book-open-line text-accent-600 text-sm"></i>
                </span>
                Kakobuy Guide
              </a>

              <a
                href="/article/cssbuy-spreadsheet-guide"
                className="flex items-center gap-3 px-4 py-3.5 text-sm font-medium text-foreground-800 hover:bg-secondary-50 rounded-lg transition-colors"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <span className="w-8 h-8 rounded-full bg-accent-100 flex items-center justify-center">
                  <i className="ri-scales-3-line text-accent-600 text-sm"></i>
                </span>
                CSSBuy Comparison
              </a>

              <a
                href="/article/superbuy-spreadsheet-guide"
                className="flex items-center gap-3 px-4 py-3.5 text-sm font-medium text-foreground-800 hover:bg-secondary-50 rounded-lg transition-colors"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <span className="w-8 h-8 rounded-full bg-accent-100 flex items-center justify-center">
                  <i className="ri-flashlight-line text-accent-600 text-sm"></i>
                </span>
                Superbuy Guide
              </a>

              <a
                href="/article/oopbuy-spreadsheet-guide"
                className="flex items-center gap-3 px-4 py-3.5 text-sm font-medium text-foreground-800 hover:bg-secondary-50 rounded-lg transition-colors"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <span className="w-8 h-8 rounded-full bg-accent-100 flex items-center justify-center">
                  <i className="ri-compass-3-line text-accent-600 text-sm"></i>
                </span>
                Oopbuy Guide
              </a>

              <div className="border-t border-background-200/70 my-3"></div>

              <div className="px-4 pt-4 pb-2">
                <p className="text-xs text-foreground-500 font-label uppercase tracking-wider mb-4">
                  Product Categories
                </p>

                <div className="space-y-3">
                  {categoryGroups.map((group) => (
                    <div key={group.label} className="rounded-lg overflow-hidden">
                      <button
                        className="w-full flex items-center justify-between px-4 py-3 bg-secondary-100/70 rounded-lg text-sm font-medium text-foreground-800 hover:bg-secondary-200/70 transition-colors cursor-pointer"
                        onClick={() =>
                          setActiveMobileGroup(
                            activeMobileGroup === group.label ? null : group.label
                          )
                        }
                      >
                        <span className="flex items-center gap-3">
                          <span className="w-6 h-6 rounded-full bg-accent-200 flex items-center justify-center">
                            <i className={`${groupIcons[group.label] || "ri-folder-line"} text-accent-600 text-xs`}></i>
                          </span>
                          {group.label}
                        </span>
                        <span className="text-xs text-foreground-500 font-medium">
                          {group.cats.length} categories
                        </span>
                      </button>

                      {activeMobileGroup === group.label && (
                        <div className="mt-1 ml-8 space-y-0.5 pb-1">
                          {group.cats.map((catIdx) => {
                            const cat = categories[catIdx];
                            return (
                              <a
                                key={cat.slug}
                                href={cat.url}
                                target="_blank"
                                rel="noopener nofollow"
                                className="flex items-center gap-3 px-3 py-2.5 text-sm text-foreground-600 hover:bg-accent-50 hover:text-accent-800 rounded-lg transition-colors cursor-pointer"
                                onClick={() => setIsMobileMenuOpen(false)}
                              >
                                <span className="w-4 h-4 flex items-center justify-center">
                                  <i className={`${cat.icon} text-xs text-foreground-400`}></i>
                                </span>
                                {cat.name}
                              </a>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <div className="border-t border-background-200/70 my-3"></div>

              <div className="flex flex-col gap-2 px-4 pt-2">
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="w-full py-3.5 text-sm text-center border border-foreground-300/60 text-foreground-800 rounded-full hover:border-foreground-500 hover:bg-background-50 transition-all cursor-pointer font-medium"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Kakobuy Sheet
                </a>
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="w-full py-3.5 text-sm text-center bg-primary-500 text-background-50 rounded-full hover:bg-primary-600 transition-all cursor-pointer font-medium"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Buy It Now
                </a>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}